/**
 * Config constants, mostly from environment variables.
 *
 * We should start moving references to process.env here and once we've moved
 * all setup a linting rule to not use process.env anywhere but here.
 */
export const IS_PRODUCTION =
  process.env.NEXT_PUBLIC_IS_PRODUCTION === "true" || false;
export const IS_LOCAL =
  process.env.NODE_ENV === "development" || process.env.IS_LOCAL === "true";
export const NEXT_APP_URL = process.env.NEXT_PUBLIC_APP_URL;

// Auth
export const NEXTAUTH_URL = process.env.NEXTAUTH_URL;
export const NEXTAUTH_SECRET = process.env.NEXTAUTH_SECRET;

// Infra
export const REDIS_URL = process.env.REDIS_URL;

// S3 Buckets (TODO: move to clients/s3/constants.ts at some point)
const S3_BUCKETS_ENV = process.env.S3_BUCKETS_ENV;
export const STUDENT_RESOURCES_BUCKET_NAME = `${S3_BUCKETS_ENV}-students-resources`;
export const GUARDIAN_RESOURCES_BUCKET_NAME = `${S3_BUCKETS_ENV}-guardians-resources`;
export const THERAPIST_RESOURCES_BUCKET_NAME = `${S3_BUCKETS_ENV}-therapist-resources`;
export const APPOINTMENT_RESOURCES_BUCKET_NAME = `${S3_BUCKETS_ENV}-appointment-resources`;
export const USER_AVATAR_BUCKET_NAME = `${S3_BUCKETS_ENV}-user-avatars`;
export const GROUP_RESOURCES_BUCKET_NAME = `${S3_BUCKETS_ENV}-groups-resources`;
export const TEXT_BLASTS_BUCKET_NAME = `${S3_BUCKETS_ENV}-text-blasts`;
export const VOICEMAIL_RECORDINGS_BUCKET_NAME = `${S3_BUCKETS_ENV}-voicemail-recordings`;
export const DESKTOP_APP_RELEASES_BUCKET_NAME = "marble-health-public";

// Desktop App Releases
export const DESKTOP_APP_RELEASES_PREFIX = "therapist-app";
export const DESKTOP_APP_MIN_VERSION = "0.1.0";

// DynamoDB Tables  (TODO: move to clients/s3/constants.ts at some point)
const DYNAMO_DB_TABLES_ENV = process.env.DYNAMO_DB_TABLES_ENV;
export const PROVIDER_CREDENTIAL_TABLE_NAME = `${DYNAMO_DB_TABLES_ENV}-provider-credentialing`;

// Client Configs (TODO: move to each client dir's constants.ts at some point)
export const NIRVANA_API_KEY = process.env.NIRVANA_API_KEY;
export const NIRVANA_ENV =
  (process.env.NIRVANA_ENV as "sandbox" | "production") || "sandbox";
export const MERCURY_ENV =
  (process.env.MERCURY_ENV as "sandbox" | "production") || "sandbox";
export const MERCURY_API_KEY = process.env.MERCURY_API_KEY;
export const MERCURY_ACCOUNT_ID = process.env.MERCURY_ACCOUNT_ID;
export const BROWSERLESS_API_TOKEN = process.env.BROWSERLESS_API_TOKEN;
export const SALESFORCE_CLIENT_ID = process.env.SALESFORCE_CLIENT_ID;
export const SALESFORCE_CLIENT_SECRET = process.env.SALESFORCE_CLIENT_SECRET;
export const DOCUSIGN_INTEGRATION_KEY = process.env.DOCUSIGN_INTEGRATION_KEY;
export const DOCUSIGN_PRIVATE_KEY = process.env.DOCUSIGN_PRIVATE_KEY;
export const DOCUSIGN_ACCOUNT_ID = process.env.DOCUSIGN_ACCOUNT_ID;
export const DOCUSIGN_USER_ID = process.env.DOCUSIGN_USER_ID;
export const DOCUSIGN_BASE_URI = process.env.DOCUSIGN_BASE_URI;
export const DOCUSIGN_DOMAIN = process.env.DOCUSIGN_DOMAIN;
export const GOOGLE_OAUTH_CLIENT_ID = process.env.GOOGLE_OAUTH_CLIENT_ID;
export const GOOGLE_OAUTH_CLIENT_SECRET =
  process.env.GOOGLE_OAUTH_CLIENT_SECRET;
export const SENDGRID_API_KEY = process.env.SENDGRID_API_KEY;

/** Local only **/
// Env configs (set by the scripts/local-dev/load-env.sh script based on keys in scripts/local-dev/secrets.json)
type LocalEnv = "prod" | "prev" | "local" | undefined;
export const LOCAL_CANDID_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_CANDID_ENV || "local") as LocalEnv);
export const LOCAL_DYNAMODB_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_DYNAMODB_ENV || "local") as LocalEnv);
export const LOCAL_POSTGRES_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_POSTGRES_ENV || "local") as LocalEnv);
export const LOCAL_REDIS_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_REDIS_ENV || "local") as LocalEnv);
export const LOCAL_S3_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.NEXT_PUBLIC_LOCAL_S3_ENV || "local") as LocalEnv);
export const LOCAL_BROWSERLESS_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_BROWSERLESS_ENV || "local") as LocalEnv);
export const LOCAL_SALESFORCE_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_SALESFORCE_ENV || "local") as LocalEnv);
export const LOCAL_TWILIO_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_TWILIO_ENV || "local") as LocalEnv);
export const LOCAL_KNOCK_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_KNOCK_ENV || "local") as LocalEnv);
export const LOCAL_ZENDESK_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_ZENDESK_ENV || "local") as LocalEnv);
export const LOCAL_DOCUSIGN_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_DOCUSIGN_ENV || "local") as LocalEnv);
export const LOCAL_SENDGRID_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_SENDGRID_ENV || "local") as LocalEnv);
// Defaults to "local" (in-process synchronous dispatch). Set
// `LOCAL_EVENTBRIDGE_ENV=prod` (via `load-env.sh --prod-eventbridge`) to
// actually create scheduled events against AWS EventBridge Scheduler in
// the prod account — useful for testing real-fire scheduling, but it
// will enqueue real prod schedules that will fire real prod handlers.
export const LOCAL_EVENTBRIDGE_ENV = !IS_LOCAL
  ? undefined
  : ((process.env.LOCAL_EVENTBRIDGE_ENV || "local") as LocalEnv);
export const SLACK_CHANNEL_OVERRIDE = process.env.SLACK_CHANNEL_OVERRIDE;
export const TESTING_TWILIO_CONVERSATION_SID =
  process.env.TESTING_TWILIO_CONVERSATION_SID;

// For some reason on some wifi networks the read replicas don't work
// so add an env var to disable them
export const LOCAL_ONLY_DISABLE_POSTGRES_READ_REPLICA =
  process.env.LOCAL_ONLY_DISABLE_POSTGRES_READ_REPLICA === "true";
